import Vue from 'vue';
import bytes from 'bytes';
import { mapGetters } from 'vuex';
import Chart from 'chart.js';
import moment from 'moment-timezone';
import dropsLoader from '@/mixins/components/drops-loader';
import tooltip from '@/mixins/components/tooltip';

export default Vue.component('site-forecast', {
  template: require('./template.html'),
  name: 'site-forecast',
  computed: {
    ...mapGetters(['currentSite']),
    progress() {
      const percentage = parseInt(this.chart.meter, 10);
      const warning = percentage > 75 && percentage <= 95;
      const danger = percentage > 95;
      return {
        danger,
        warning,
      };
    },
    colors() {
      let border = '#1ebda0';
      let bg = 'rgba(69, 223, 195, .3)';
      if (this.progress.danger && !this.isPremium) {
        border = '#ff4d23';
        bg = 'rgba(255, 95, 95, .45)';
      }
      if (this.progress.warning && !this.isPremium) {
        border = '#f98d40';
        bg = 'rgba(249, 141, 64, .45)';
      }
      return { border, bg };
    },
    isPremium() {
      return this.currentSite.tier === 'Premium';
    },
  },
  components: {
    dropsLoader,
    tooltip,
  },
  data() {
    return {
      loading: false,
      forecast: {},
      ranges: {},
      currentRange: [],
      fetchError: false,
      limitThreshold: 100,
      threshold: 100,
      billingRange: ['None Available'],
      selectedRange: 'None Available',
      chart: {
        meter: 0,
        instance: null,
        rangeLegend: [],
        config: {},
        data: [],
      },
      currentRangeObj: {},
      range: {
        start: '',
        end: '',
      },
    };
  },
  mounted() {
    this.getForecast();
  },
  methods: {
    async getForecast() {
      this.loading = true;
      this.fetchError = false;
      try {
        this.forecast = await this.$lightning([{
          name: 'getForecast',
          payload: { siteId: this.currentSite._id },
        }]);
        // convert times to CST
        this.ranges = this.forecast.ranges;
        if (this.forecast.sorted.length > 0) {
          // gather other range options
          this.billingRange = this.forecast.sorted.map(d => this.forecast.startKeys[d]);
          // gather current range data
          const firstKey = this.forecast.sorted[0];
          const startKey = this.forecast.startKeys[firstKey];
          this.selectedRange = startKey;
          const [, startDate, stopDate] = startKey.split('_');
          this.range.start = startDate;
          this.range.end = stopDate;
          const currentRangeObj = this.forecast.ranges[startKey];
          const placeholder = [{
            bytes: 0,
            date: startDate,
          }];
          if (currentRangeObj.byDay) {
            this.currentRange = currentRangeObj.byDay;
            this.$log.info('range by day: ', this.currentRange);
            this.currentRangeObj = currentRangeObj;
            const bytesTotal = this.convertBytes(currentRangeObj.bytes, 'GB');
            this.chart.meter = ((parseInt(bytesTotal, 10) * 100) / this.threshold).toFixed(0);
          } else {
            this.currentRange = [placeholder];
            this.currentRangeObj.bytes = 0;
            this.chart.meter = 0;
          }
          this.chart.rangeLegend = this.currentRange.map(day => moment.utc(day.date).format('MM/DD'));
        }
        const data = {
          labels: this.chart.rangeLegend,
          datasets: [
            {
              label: 'Bandwidth (MB)',
              data: this.currentRange.map(day => this.convertBytes(day.bytes)),
              backgroundColor: this.colors.bg,
              borderColor: this.colors.border,
              borderWidth: 0.5,
            },
          ],
        };
        this.chart.config = {
          type: 'bar',
          data,
          options: {
            animation: {
              easing: 'linear',
              duration: 400,
            },
            legend: {
              position: 'top',
              labels: {
                fontColor: '#bec0c2',
              },
            },
            title: {
              display: true,
              fontColor: '#bec0c2',
              text: 'Bandwidth Per Billing Period (UTC)',
            },
            plugins: {
            },
            scales: {
              yAxes: [{
                ticks: {
                  fontColor: '#bec0c2',
                  beginAtZero: true,
                },
                gridLines: {
                  color: '#414344',
                },
              }],
              xAxes: [{
                stacked: true,
                ticks: {
                  fontColor: '#bec0c2',
                },
                gridLines: {
                  display: false,
                },
              }],
            },
            responsive: true,
            maintainAspectRatio: false,
          },
        };
      } catch (e) {
        const message = e.message || e;
        this.$log.error(message);
        this.fetchError = message;
      } finally {
        this.loading = false;
        setTimeout(() => {
          this.chart.instance = new Chart(this.$refs.forecast, this.chart.config);
        }, 200);
      }
    },
    applyNewRange() {
      if (this.selectedRange !== 'None Available') {
        const startKey = this.selectedRange;
        const [, startDate, stopDate] = startKey.split('_');
        this.range.start = startDate;
        this.range.end = stopDate;
        const currentRangeObj = this.forecast.ranges[startKey];
        const placeholder = [{
          bytes: 0,
          date: startDate,
        }];
        if (currentRangeObj.byDay) {
          this.currentRange = currentRangeObj.byDay;
          this.currentRangeObj = currentRangeObj;
          const bytesTotal = this.convertBytes(currentRangeObj.bytes, 'GB');
          this.chart.meter = ((parseInt(bytesTotal, 10) * 100) / this.threshold).toFixed(0);
        } else {
          this.currentRange = [placeholder];
          this.currentRangeObj.bytes = 0;
          this.chart.meter = 0;
        }
        this.chart.rangeLegend = this.currentRange.map(day => moment.utc(day.date).format('MM/DD'));
        this.chart.config.data.labels = this.chart.rangeLegend;
        this.chart.config.data.datasets[0].data = this.currentRange.map(
          day => this.convertBytes(day.bytes),
        );
        this.chart.config.data.datasets[0].backgroundColor = this.colors.bg;
        this.chart.config.data.datasets[0].borderColor = this.colors.border;
        this.chart.instance.update(this.chart.config);
        setTimeout(() => this.chart.instance.update(this.chart.config), 100);
      }
    },
    getDaysInRange(rangeKey) {
      const [, startDate, stopDate] = rangeKey.split('_');
      this.range.start = startDate;
      this.range.end = stopDate;
      const dates = [];
      let currentDate = moment(startDate);
      const stop = moment(stopDate);
      while (currentDate <= stop) {
        dates.push(moment(currentDate).format('MM/DD'));
        currentDate = moment(currentDate).add(1, 'days');
      }
      return dates;
    },
    getDateText(rangeKey) {
      const [, startDate, stopDate] = rangeKey.split('_');
      const start = this.formatRange(startDate);
      const end = this.formatRange(stopDate);
      return `${start} - ${end}`;
    },
    convertBytes(num, unit) {
      return (bytes(num, { unit: unit || 'MB' }) || '0').replace(/[^\d.-]/g, '');
    },
    formatRange(date) {
      return moment(date).format('MMM DD, YYYY');
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/admin/websites/edit-site/forecast/index.js